import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useCart } from '../../hooks/useCart';
import { useAddresses } from '../../hooks/useAddresses';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/common/Toast';
import { orderService } from '../../services/order.service';
import { paymentService } from '../../services/payment.service';
import { theme } from '../../config/theme';
import { formatCurrency } from '../../utils/helpers';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// Helper to load Razorpay Checkout script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Checkout = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { user } = useAuth();
  const { items, summary, loading: cartLoading, clearCart } = useCart();
  const { addresses, loading: addrsLoading } = useAddresses();
  
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Pre-select default address if available
  useEffect(() => {
    if (addresses && addresses.length > 0 && !selectedAddressId) {
      const defaultAddr = addresses.find(a => a.is_default);
      if (defaultAddr) {
        setSelectedAddressId(defaultAddr.id);
      } else {
        setSelectedAddressId(addresses[0].id);
      }
    }
  }, [addresses, selectedAddressId]);

  // Load Razorpay Script on mount
  useEffect(() => {
    loadRazorpayScript();
  }, []);

  if (cartLoading || addrsLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (items.length === 0) {
    return (
      <MainLayout>
        <div style={{ padding: isMobile ? theme.spacing.md : theme.spacing.xl, textAlign: 'center' }}>
          <h2>Your cart is empty</h2>
          <Button onClick={() => navigate('/products')} style={{ marginTop: theme.spacing.md }}>
            Continue Shopping
          </Button>
        </div>
      </MainLayout>
    );
  }

  const handlePlaceOrder = async () => {
    if (!selectedAddressId) {
      toast.error('Please select a shipping address.');
      return;
    }

    setIsProcessing(true);

    try {
      // 1. Create Order Backend Record
      const orderRes = await orderService.createOrder({
        shipping_address_id: selectedAddressId,
        billing_address_id: selectedAddressId,
        payment_method: 'razorpay'
      });
      
      const orderDetails = orderRes.data.data.order;

      // 2. Initiate Payment (Creates Razorpay Order)
      const initRes = await paymentService.initiatePayment(orderDetails.id, 'razorpay');
      const { razorpayOrder, method } = initRes.data.data;

      // Ensure script is loaded
      if (!window.Razorpay) {
        toast.error('Razorpay SDK failed to load. Are you online?');
        setIsProcessing(false);
        return;
      }

      // 3. Initialize Razorpay Modal
      const options = {
        key: razorpayOrder.key,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: "AlarcTrade Platform",
        description: `Order ${orderDetails.order_number}`,
        order_id: razorpayOrder.id,
        handler: async function (response) {
          try {
            // 4. Verify Payment on Backend
            await paymentService.verifyRazorpay({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: orderDetails.id
            });
            
            // Clear cart silently & Redirect
            await clearCart(true);
            navigate('/order-success?payment=success');
          } catch (err) {
            console.error('Payment verification failed:', err);
            toast.error('Payment verification failed. Please contact support if amount was deducted.');
            navigate('/order-success?payment=failed');
          }
        },
        prefill: {
          name: user ? `${user.first_name} ${user.last_name}` : "",
          email: user ? user.email : "",
          contact: user && user.phone ? user.phone : ""
        },
        theme: {
          color: theme.colors.primary.main
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
            toast.error('Payment cancelled');
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on('payment.failed', function (response){
        toast.error(`Payment Failed: ${response.error.description}`);
        setIsProcessing(false);
      });

      razorpayInstance.open();

    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.response?.data?.message || 'Failed to place order. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? theme.spacing.md : theme.spacing.xl, maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: 'bold', marginBottom: theme.spacing.xl }}>Checkout</h1>
        
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 400px', gap: isMobile ? theme.spacing.lg : theme.spacing.xl }}>
          {/* Left Column - Address */}
          <div>
            <Card padding="lg" style={{ marginBottom: theme.spacing.xl }}>
              <h2 style={{ fontSize: theme.typography.fontSize.xl, marginBottom: theme.spacing.md }}>Shipping Address</h2>
              {addresses.length === 0 ? (
                <div>
                  <p style={{ color: theme.colors.text.secondary, marginBottom: theme.spacing.md }}>No addresses found.</p>
                  <Button variant="outline" onClick={() => navigate('/addresses')}>Add Address</Button>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: theme.spacing.md }}>
                  {addresses.map(addr => (
                    <div 
                      key={addr.id} 
                      onClick={() => setSelectedAddressId(addr.id)}
                      style={{ 
                        border: `1px solid ${selectedAddressId === addr.id ? theme.colors.primary.main : theme.colors.gray[300]}`,
                        borderRadius: theme.borderRadius.md,
                        padding: theme.spacing.md,
                        cursor: 'pointer',
                        backgroundColor: selectedAddressId === addr.id ? `${theme.colors.primary.main}10` : 'transparent'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                        <input 
                          type="radio" 
                          checked={selectedAddressId === addr.id}
                          onChange={() => setSelectedAddressId(addr.id)}
                          style={{ accentColor: theme.colors.primary.main }}
                        />
                        <span style={{ fontWeight: 'bold' }}>{addr.first_name} {addr.last_name}</span>
                        {addr.is_default && <span style={{ fontSize: '12px', background: theme.colors.primary.main, color: 'white', padding: '2px 6px', borderRadius: '4px' }}>Default</span>}
                      </div>
                      <div style={{ paddingLeft: '24px', marginTop: theme.spacing.xs, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                        <div>{addr.address_line1} {addr.address_line2}</div>
                        <div>{addr.city}, {addr.state} {addr.postal_code}</div>
                        <div>Phone: {addr.phone_number}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Right Column - Summary & Payment */}
          <div>
            <Card padding="lg">
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: 'bold', marginBottom: theme.spacing.md }}>Order Summary</h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm, marginBottom: theme.spacing.lg }}>
                {items.map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span>{item.product.name} x {item.quantity}</span>
                    <span>{formatCurrency(item.product.price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${theme.colors.gray[200]}`, paddingTop: theme.spacing.md, marginBottom: theme.spacing.lg, fontWeight: 'bold', fontSize: theme.typography.fontSize.lg }}>
                <span>Total</span>
                <span style={{ color: theme.colors.primary.main }}>{formatCurrency(summary.subtotal)}</span>
              </div>

              <Button 
                variant="primary" 
                fullWidth 
                size="lg"
                onClick={handlePlaceOrder}
                loading={isProcessing}
                disabled={!selectedAddressId || items.length === 0}
              >
                Place Order & Pay
              </Button>
              <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, textAlign: 'center', marginTop: theme.spacing.sm }}>
                Secure Payment by Razorpay
              </div>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Checkout;
